import styled from "styled-components";
import Button from "../../Atoms/Button";
import { tokens, media } from "../../../design/tokens";

/*
 * Segundo drawer da compra — o que abre ao escolher um metodo no carrinho.
 *
 * Ate agora ficava branco enquanto o carrinho ao lado ja era escuro: o aluno
 * escolhia "PIX" num painel preto e caia num painel branco, a um clique de
 * distancia. A emenda so existia porque metade do fluxo tinha sido tratada.
 *
 * Estilo apenas: a logica de cobranca do index.tsx (1856 linhas) nao e tocada.
 */
export const Container = styled.div`
  display: flex;
  flex-direction: column;

  width: 500px;
  height: 100%;

  position: absolute;
  right: 0;

  background: ${tokens.color.cardCharcoal};
  border-radius: ${tokens.radius.sm} 0 0 ${tokens.radius.sm};
  box-shadow: ${tokens.shadow.imageSeat};

  z-index: 5;

  ${media.upTo(tokens.breakpoint.sm)} {
    max-width: 100%;
    width: 100%;
    min-height: 100%;
    height: auto;
  }
`;

/* Mesma decisao do carrinho: o cabecalho deixa de ser uma faixa cheia de ouro.
   O acento fica guardado para o valor e para a acao que fecha a compra. */
export const TitleSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  height: 56px;
  width: 100%;

  background: ${tokens.color.cardCharcoal};
  border-bottom: 1px solid ${tokens.color.fieldSlate};
  border-radius: ${tokens.radius.sm} 0 0 0;

  color: ${tokens.color.textWhite};

  font-family: ${tokens.font.label.family};
  font-size: 1rem;
  font-weight: 600;

  .close_btn {
    position: absolute;
    top: 18px;
    left: ${tokens.space.lg};
    color: ${tokens.color.textWhite};

    transition: filter 0.2s, color 0.2s;

    &:hover {
      filter: opacity(0.6);
      cursor: pointer;
    }
  }

  .back_btn {
    position: absolute;
    top: 18px;
    left: 64px;
    color: ${tokens.color.textWhite};

    transition: filter 0.2s;

    &:hover {
      filter: opacity(0.6);
      cursor: pointer;
    }
  }
`;

export const ItensSection = styled.div`
  /* Era min-height de 93.5vh — um numero que so fazia sentido com o cabecalho
     de 40px do painel antigo, e que forcava rolagem em janela baixa. Agora
     ocupa o que sobra do painel. */
  flex: 1;
  min-height: 0;
  overflow-y: auto;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  gap: ${tokens.space.md};

  padding: ${tokens.space.xl} ${tokens.space.xl} 96px;

  color: ${tokens.color.textWhite};
  font-family: ${tokens.font.body.family};

  /*
   * UMA COLUNA SO.
   *
   * O defeito que motivou esta passada: tres larguras concorrentes empilhadas
   * — dropdown em 100%, campos em 75% (o padrao do atomo Input) e botao em
   * 240px. A borda direita ficava serrilhada.
   *
   * Agora o form define a coluna e tudo que e controle a preenche. O
   * :not(.rccs) e essencial: o cartao ilustrado do react-credit-cards
   * tambem e filho direto do form, e seria esticado junto.
   */
  form {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: stretch;

    gap: ${tokens.space.lg};
  }

  form > div:not(.rccs),
  form > button {
    width: 100%;
  }

  /* O gap acima decide o ritmo; a margem propria do Input/Button somaria. */
  form > div,
  form > button {
    margin-top: 0;
  }

  .dropdown-wrapper {
    width: 100%;
  }

  /*
   * Linha de campos curtos. Eram 60px fixos cada; agora dividem a linha.
   * So sobrevivem pareados os dados curtos POR NATUREZA — CEP + numero,
   * validade + CVV. E a regra que o Perfil aprendeu na pratica: campo
   * estreito com dado longo trunca sem aviso.
   */
  .group-info {
    display: flex;
    width: 100%;
    margin: 0;
    align-items: flex-start;
    justify-content: space-between;
    gap: ${tokens.space.md};

    > div {
      margin: 0;
      flex: 1;
      min-width: 0;
      width: auto;
    }
  }

  /*
   * Cartao ilustrado do react-credit-cards.
   *
   * ⚠️ ACOPLAMENTO CONSCIENTE A DEPENDENCIA DE TERCEIRO.
   * A lib traz styles-compiled.css e renderiza um retangulo cinza claro que
   * nao pertence a paleta. Nao ha prop de tema: a unica via e sobrescrever as
   * classes internas abaixo.
   *
   * O RISCO: se a lib for atualizada e renomear estas classes, o tema some em
   * SILENCIO — o cartao volta ao cinza, sem erro de build e sem falha de
   * teste. Auditar aqui ao mexer em react-credit-cards.
   */
  div.rccs {
    margin: 0 auto;
  }

  div.rccs__card--unknown > div,
  div.rccs__card--front,
  div.rccs__card--back {
    background: linear-gradient(
      135deg,
      ${tokens.color.fieldSlate} 0%,
      ${tokens.color.railGraphite} 100%
    );
    box-shadow: ${tokens.shadow.imageSeat};
  }

  div.rccs__number,
  div.rccs__name,
  div.rccs__expiry__value,
  div.rccs__cvc {
    color: ${tokens.color.textWhite};
  }

  div.rccs__expiry__valid {
    color: ${tokens.color.textAsh};
  }

  @media (max-height: 700px) {
    div.rccs > div.rccs__card {
      max-height: 120px;
      margin: 15px 15px 15px 0;
    }
  }

  ${media.upTo(tokens.breakpoint.sm)} {
    .form.big {
      margin-bottom: 5rem;
    }
  }
`;

/*
 * Campo rotulado — mesma convencao ja adotada em Login, Password, SignUp e
 * Perfil (ver UserProfile/styles.ts).
 *
 * Aqui ela vale ainda mais que no Perfil. La o problema era o campo nascer
 * preenchido; neste formulario o rotulo some assim que o usuario DIGITA,
 * porque placeholder so aparece com o campo vazio. Depois de preencher, nao
 * havia como distinguir "Data de validade" de "CVV" — defeito funcional num
 * formulario de pagamento, nao questao de gosto.
 */
export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${tokens.space.xs};

  width: 100%;
  min-width: 0;
`;

/*
 * <span>, e nao um segundo <label htmlFor>: o atomo Input ja registra o rotulo
 * acessivel (oculto visualmente) a partir do placeholder, e dois <label>
 * apontando para o mesmo campo tem leitura inconsistente entre leitores de
 * tela. O placeholder continua no lugar, com o MESMO texto, para os dois
 * nunca divergirem.
 */
export const FieldLabel = styled.span`
  font-family: ${tokens.font.caption.family};
  font-size: ${tokens.font.caption.size};
  font-weight: 600;
  color: ${tokens.color.textAsh};
`;

export const QrArea = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  align-items: center;
  gap: ${tokens.space.lg};

  color: ${tokens.color.textWhite};

  /* O QR do PIX e lido por camera: precisa de fundo claro atras dos modulos,
     entao a moldura branca aqui e funcional, nao decorativa. */
  img,
  canvas,
  svg {
    background: ${tokens.color.textWhite};
    padding: ${tokens.space.md};
    border-radius: ${tokens.radius.sm};
  }

  .total {
    display: flex;
    align-items: center;
    justify-content: center;

    strong {
      margin-right: 1.5rem;
      color: ${tokens.color.marqueeGold};
      font-size: 1.125rem;
    }
  }
`;

export const AddressWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  align-items: center;

  width: 100%;
  margin: 0 auto;

  color: ${tokens.color.textWhite};

  /* Era background var(--primary_cyan_color) — o ciano vestigial que o
     DESIGN.md manda explicitamente nao usar em nada novo. */
  /* Salvar/Cancelar ficam lado a lado: aqui o teto de 240px faz sentido.
     Dentro do <form>, o botao de pagar preenche a coluna como os campos. */
  button {
    margin-top: 0;
    max-width: 240px;
    color: ${tokens.color.inkCharcoal};
    background: ${tokens.color.marqueeGold};
  }

  p {
    font-size: ${tokens.font.body.size};
    margin: 0.2rem 0.2px 1rem 0.2rem;
    text-align: center;

    & > svg {
      font-size: 1.3rem;
      color: ${tokens.color.marqueeGold};

      cursor: pointer;

      margin: 0 0 0 10px;
      vertical-align: bottom;
    }

    ${media.upTo(tokens.breakpoint.sm)} {
      margin: 0.5rem 0;
    }
  }
`;

/* A barra de acao fica colada no rodape do painel, sobre o degrau tonal, para
   nao flutuar por cima do formulario que rola atras dela. */
export const ButtonSection = styled.div`
  width: 100%;

  display: flex;
  flex-direction: row;
  justify-content: space-around;
  gap: ${tokens.space.md};

  position: absolute;
  bottom: 0;
  left: 0;

  padding: ${tokens.space.lg} ${tokens.space.xl};
  background: ${tokens.color.cardCharcoal};
  border-top: 1px solid ${tokens.color.fieldSlate};
`;

export const StyledButton = styled(Button)`
  display: flex;
  justify-content: center;
  align-items: center;

  /*
   * Eram 240px fixos, que dois lado a lado estouravam o painel. Uma versao
   * intermediaria usou flex: 1, mas estes botoes tambem aparecem empilhados
   * num pai em coluna, onde flex nao governa a largura — e eles encolhiam
   * para o tamanho do texto ("Salvar" virava 101px).
   * Largura da coluna com teto: funciona nos dois arranjos.
   */
  width: 100%;
  min-width: 0;

  p {
    margin-left: ${tokens.space.sm};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;
